import { termMarketsByChain } from '@1delta/data-sdk'
import { buildTermLensCall, usesTermLens } from './lens'
import { Call } from '../../../utils/multicall'

/**
 * Number of multicall slots one Term repo consumes: `repoToken.balanceOf(user)`
 * (1) + `repoToken.redemptionValue()` (1) + `servicer.getBorrowerRepurchaseObligation(user)`
 * (1) + one `collateralManager.getCollateralBalance(user, token)` per collateral
 * leg. The parser walks `termMarketsByChain` in the SAME order to slice results.
 */
export const termMarketCallCount = (collateralCount: number) => 3 + collateralCount

/**
 * Build the user-data multicall for every configured Term repo on a chain.
 *
 * Unlike Midnight (one core contract), Term positions live across the repo's
 * serial contracts: lent principal from the TermRepoToken (`balanceOf` scaled by
 * `redemptionValue`), borrow debt from the TermRepoServicer
 * (`getBorrowerRepurchaseObligation`), and collateral from the
 * TermRepoCollateralManager (`getCollateralBalance` per leg). Function names do
 * not collide across the three ABIs, so a single merged ABI (see `getAbi`)
 * resolves each call by name while each call targets its own contract address.
 */
export const buildTermUserCall = (
  chainId: string,
  _lender: string,
  account: string,
): Call[] => {
  const markets = termMarketsByChain(chainId)
  if (markets.length === 0) return []

  // A chain without a lens deployment keeps the per-repo walk — the rollout
  // switch and the emergency revert both.
  if (usesTermLens(chainId)) {
    const lensCall = buildTermLensCall(chainId, markets as any, account)
    if (lensCall.length) return lensCall
  }

  const calls: Call[] = []
  for (const market of markets) {
    calls.push({ address: market.repoToken, name: 'balanceOf', params: [account] })
    calls.push({ address: market.repoToken, name: 'redemptionValue', params: [] })
    calls.push({
      address: market.servicer,
      name: 'getBorrowerRepurchaseObligation',
      params: [account],
    })
    for (const c of market.collateralParams) {
      calls.push({
        address: market.collateralManager,
        name: 'getCollateralBalance',
        params: [account, c.token],
      })
    }
  }
  return calls
}
